import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock, Users, Loader2, Wine, Ticket, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TIME_SLOTS, isOpenDay, toLocalDateStr, fmtDate } from "@/lib/booking";
import { useConfig } from "@/lib/useConfig";

export default function BookingSection() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", guests: 2, cheeseboards: 0, wine_club: false });
  const [submitting, setSubmitting] = useState(false);
  const [voucher, setVoucher] = useState("");
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [voucherMsg, setVoucherMsg] = useState(null);
  const [checkingVoucher, setCheckingVoucher] = useState(false);
  const { payments_enabled } = useConfig();
  const payOnArrival = !payments_enabled;

  const guestsNum = Number(form.guests) || 0;
  const cheeseNum = Number(form.cheeseboards) || 0;
  const tastingCost = guestsNum * 15;
  const cheeseCost = cheeseNum * 42;
  const discount = voucherApplied ? tastingCost : 0;
  const dueNow = Math.max(0, tastingCost + cheeseCost - discount);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledMatcher = (d) => d < today || !isOpenDay(d);

  const resetVoucher = (val) => {
    setVoucher(val);
    if (voucherApplied) setVoucherApplied(false);
    if (voucherMsg) setVoucherMsg(null);
  };

  const applyVoucher = async () => {
    const code = voucher.trim();
    if (!code) return;
    setCheckingVoucher(true);
    try {
      const res = await api.post("/vouchers/validate", { code });
      if (res.data.valid) {
        setVoucherApplied(true);
        setVoucherMsg({ ok: true, text: res.data.message });
      } else {
        setVoucherApplied(false);
        setVoucherMsg({ ok: false, text: res.data.message });
      }
    } catch (err) {
      setVoucherApplied(false);
      setVoucherMsg({ ok: false, text: "Could not check voucher. Please try again." });
    } finally {
      setCheckingVoucher(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guests: Number(form.guests),
        cheeseboards: Number(form.cheeseboards),
        date: toLocalDateStr(date),
        time_slot: slot,
        origin_url: window.location.origin,
      };
      if (voucherApplied && voucher.trim()) payload.voucher_code = voucher.trim();
      const res = await api.post("/payments/create-checkout", payload);
      if (res.data.payment_required === false) {
        window.location.href = `/booking/success?token=${res.data.manage_token}`;
      } else {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment.");
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#4A5D23]">Reserve Your Visit</p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-[#1C1917]">
            Book a Wine Tasting
          </h2>
          <p className="mt-4 text-[#57534E] text-lg">
            Choose a date, pick your hour, and we'll pour the rest.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left: calendar + slots */}
          <div className="lg:col-span-5 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#E7E5E4]">
            <div className="flex items-center gap-2 mb-4 text-[#1C1917] font-medium">
              <CalendarDays className="h-5 w-5 text-[#4A5D23]" /> Select a date
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                setDate(d);
                setSlot(null);
              }}
              disabled={disabledMatcher}
              data-testid="booking-calendar"
              className="rounded-xl"
            />
            <p className="mt-3 text-xs text-[#57534E]">
              Open Thursday–Saturday & the first Sunday of each month, 11am–2pm.
            </p>

            {date && (
              <div className="mt-6 animate-fade-up">
                <div className="flex items-center gap-2 mb-3 text-[#1C1917] font-medium">
                  <Clock className="h-5 w-5 text-[#4A5D23]" /> Select a time
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {TIME_SLOTS.filter((t) => !(date && date.getDay() === 6 && t === "3:00 PM")).map((t) => (
                    <button
                      key={t}
                      data-testid={`time-slot-${t.replace(/[:\s]/g, "").toLowerCase()}`}
                      onClick={() => setSlot(t)}
                      className={`py-3 px-2 rounded-xl border text-sm font-medium transition-all ${
                        slot === t
                          ? "bg-[#4A5D23] text-white border-[#4A5D23]"
                          : "border-[#E7E5E4] text-[#57534E] hover:border-[#4A5D23] hover:text-[#4A5D23]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-[#E7E5E4]">
            {!date || !slot ? (
              <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-[#4A5D23]/10 flex items-center justify-center mb-4">
                  <Wine className="h-7 w-7 text-[#4A5D23]" />
                </div>
                <h3 className="font-serif text-2xl text-[#1C1917]">Your details</h3>
                <p className="mt-2 text-[#57534E] max-w-sm">
                  Pick a date and time on the left, then tell us who's coming and
                  add an optional cheeseboard.
                </p>
                <div className="mt-6 w-full max-w-sm space-y-2 text-sm">
                  <div className="flex justify-between rounded-lg bg-[#FDFBF7] border border-[#E7E5E4] px-4 py-2.5">
                    <span className="text-[#57534E]">Wine tasting</span>
                    <span className="font-semibold text-[#1C1917]">$15.00 per person</span>
                  </div>
                  <div className="flex justify-between rounded-lg bg-[#FDFBF7] border border-[#E7E5E4] px-4 py-2.5">
                    <span className="text-[#57534E]">Cheeseboard (serves two)</span>
                    <span className="font-semibold text-[#1C1917]">$42.00 each</span>
                  </div>
                  <p className="text-xs text-[#57534E] pt-1">{payOnArrival ? "No payment is required now — payment will be collected upon arrival at the cellar door." : "Full payment is collected securely online to confirm your booking."}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="animate-fade-up" data-testid="booking-form">
                <div className="flex items-center gap-2 mb-6 text-sm bg-[#FDFBF7] border border-[#E7E5E4] rounded-xl px-4 py-3">
                  <CalendarDays className="h-4 w-4 text-[#4A5D23]" />
                  <span className="font-medium text-[#1C1917]">{fmtDate(date)}</span>
                  <span className="text-[#57534E]">·</span>
                  <Clock className="h-4 w-4 text-[#4A5D23]" />
                  <span className="font-medium text-[#1C1917]">{slot}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Full name</label>
                    <input
                      required
                      data-testid="input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Winemaker"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#4A5D23]/20 focus:border-[#4A5D23] transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Phone</label>
                    <input
                      required
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="0400 000 000"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#4A5D23]/20 focus:border-[#4A5D23] transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Email</label>
                    <input
                      required
                      type="email"
                      data-testid="input-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#4A5D23]/20 focus:border-[#4A5D23] transition-colors outline-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-4 w-4" /> Number of guests
                      </span>
                    </label>
                    <input
                      required
                      type="number"
                      min="1"
                      max="50"
                      data-testid="input-guests"
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#4A5D23]/20 focus:border-[#4A5D23] transition-colors outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <div className="rounded-xl border border-[#E7E5E4] bg-[#FDFBF7] p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-2">
                          <Wine className="h-4 w-4 mt-0.5 text-[#4A5D23]" />
                          <div>
                            <p className="font-semibold text-[#1C1917]">Add a cheeseboard</p>
                            <p className="text-sm text-[#57534E]">$42.00 each · serves two people</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            data-testid="cheeseboard-decrement"
                            onClick={() => setForm({ ...form, cheeseboards: Math.max(0, Number(form.cheeseboards) - 1) })}
                            className="h-9 w-9 rounded-full border border-[#E7E5E4] bg-white text-[#1C1917] text-lg font-semibold hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors disabled:opacity-40"
                            disabled={Number(form.cheeseboards) <= 0}
                          >
                            −
                          </button>
                          <span data-testid="cheeseboard-count" className="w-8 text-center font-semibold text-[#1C1917]">
                            {form.cheeseboards}
                          </span>
                          <button
                            type="button"
                            data-testid="cheeseboard-increment"
                            onClick={() => setForm({ ...form, cheeseboards: Number(form.cheeseboards) + 1 })}
                            className="h-9 w-9 rounded-full border border-[#E7E5E4] bg-white text-[#1C1917] text-lg font-semibold hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-[#722F37]/25 bg-[#722F37]/5 p-4" data-testid="voucher-section">
                  <label className="flex items-center gap-2 text-sm font-semibold text-[#1C1917] mb-2">
                    <Ticket className="h-4 w-4 text-[#722F37]" /> Gift voucher number
                  </label>
                  <p className="text-xs text-[#57534E] mb-3">Have a gift voucher? Enter it to cover your wine tasting cost.</p>
                  <div className="flex gap-2">
                    <input
                      data-testid="input-voucher"
                      value={voucher}
                      onChange={(e) => resetVoucher(e.target.value)}
                      placeholder="e.g. VNCDP0109XXXX"
                      disabled={voucherApplied}
                      className="flex-1 px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] transition-colors outline-none disabled:bg-[#F5F5F4] disabled:text-[#57534E] uppercase"
                    />
                    <button
                      type="button"
                      data-testid="apply-voucher-btn"
                      onClick={applyVoucher}
                      disabled={checkingVoucher || voucherApplied || !voucher.trim()}
                      className="px-5 py-3 rounded-xl bg-[#722F37] text-white font-semibold text-sm hover:bg-[#5E262D] transition-colors disabled:opacity-50 flex items-center gap-2 shrink-0"
                    >
                      {checkingVoucher ? <Loader2 className="h-4 w-4 animate-spin" /> : voucherApplied ? <Check className="h-4 w-4" /> : null}
                      {voucherApplied ? "Applied" : "Apply"}
                    </button>
                  </div>
                  {voucherMsg && (
                    <p
                      data-testid="voucher-message"
                      className={`mt-2 text-sm font-medium ${voucherMsg.ok ? "text-[#4A5D23]" : "text-[#722F37]"}`}
                    >
                      {voucherMsg.text}
                    </p>
                  )}
                </div>

                <div className="mt-5 rounded-xl border border-[#4A5D23]/20 bg-[#4A5D23]/5 p-4 text-sm" data-testid="pricing-summary">
                  <div className="flex justify-between text-[#57534E]">
                    <span>Wine tasting · $15.00 per person × {form.guests || 0}</span>
                    <span className="font-medium text-[#1C1917]">${tastingCost.toFixed(2)}</span>
                  </div>
                  {cheeseNum > 0 && (
                    <div className="flex justify-between text-[#57534E] mt-1.5">
                      <span>Cheeseboards · $42.00 × {form.cheeseboards}</span>
                      <span className="font-medium text-[#1C1917]">${cheeseCost.toFixed(2)}</span>
                    </div>
                  )}
                  {voucherApplied && (
                    <div className="flex justify-between text-[#722F37] mt-1.5" data-testid="voucher-discount-row">
                      <span>Gift voucher · wine tasting covered</span>
                      <span className="font-medium">−${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t border-[#4A5D23]/20 font-semibold text-[#1C1917]">
                    <span>{payOnArrival ? "Due on arrival" : voucherApplied ? "Balance owing" : "Estimated total"}</span>
                    <span data-testid="estimated-total">
                      ${dueNow.toFixed(2)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[#57534E]">
                    {payOnArrival
                      ? "No payment is required now — payment will be collected upon arrival at the cellar door."
                      : dueNow === 0
                      ? "Your voucher covers the full amount — no payment required."
                      : "Full payment is collected securely online to confirm your booking."}
                  </p>
                </div>

                <label
                  data-testid="wine-club-opt-in"
                  className="mt-6 flex items-start gap-3 p-4 rounded-xl border border-[#E7E5E4] bg-[#722F37]/5 cursor-pointer hover:border-[#722F37]/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={form.wine_club}
                    onChange={(e) => setForm({ ...form, wine_club: e.target.checked })}
                    className="mt-1 h-5 w-5 accent-[#722F37]"
                    data-testid="wine-club-checkbox"
                  />
                  <span>
                    <span className="block font-semibold text-[#1C1917]">Join the Gledswood Wine Club</span>
                    <span className="block text-sm text-[#57534E]">
                      Member pricing, early releases & exclusive cellar door events.
                    </span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-booking-btn"
                  className="mt-6 w-full py-4 px-8 bg-[#4A5D23] text-white rounded-full font-semibold text-lg hover:bg-[#3D4D1D] transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> {payOnArrival || dueNow === 0 ? "Confirming your booking…" : "Redirecting to secure checkout…"}
                    </>
                  ) : (
                    <>{payOnArrival || dueNow === 0 ? "Confirm Booking" : <>Pay ${dueNow.toFixed(2)} &amp; Confirm</>}</>
                  )}
                </button>
                <p className="mt-3 text-center text-xs text-[#57534E]">
                  {payOnArrival
                    ? "No payment now — payment is collected on arrival at the cellar door."
                    : dueNow === 0
                    ? "Your gift voucher covers the full amount — no card required."
                    : "Secure payment powered by Stripe. You'll be redirected to complete your booking."}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
